// ** React Imports
import { useState, SyntheticEvent, Fragment } from 'react'

// import { useRouter } from 'next/router'

// ** MUI Imports
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import Badge from '@mui/material/Badge'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem'

import Icon from 'src/components/ui/icon'

import { useAccountAction, useAccountSelect } from 'src/hooks/useAccount'
import { Settings } from 'src/context/settingsContext'
import { getAvatarSx } from 'src/components/common/personaAvatar'
import { getUserPrimaryLabel } from 'src/utils/getUserPrimaryLabel'
import { useNavigate } from 'react-router-dom'

interface Props {
  settings: Settings
}

const BadgeContentSpan = styled('span')(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
}))

const MenuItemStyled = styled(MenuItem)<MenuItemProps>(({ theme }) => ({
  '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
    color: theme.palette.primary.main,
  },
}))

const UserDropdown = (props: Props) => {
  const logoutAction = useAccountAction.logout()
  const user = useAccountSelect.user()
  const navigate = useNavigate()
  const { settings } = props
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const { direction } = settings
  const name = getUserPrimaryLabel(user)
  const hasAvatar = Boolean(user?.info?.avatar)

  const handleDropdownOpen = (event: SyntheticEvent) => {
    setAnchorEl(event.currentTarget)
  }

  const handleDropdownClose = () => {
    setAnchorEl(null)
  }

  const styles = {
    px: 4,
    py: 1.75,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    color: 'text.primary',
    textDecoration: 'none',
    '& svg': {
      mr: 2.5,
      fontSize: '1.5rem',
      color: 'text.secondary',
    },
  }

  const handleLogout = () => {
    logoutAction()
    handleDropdownClose()
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    handleDropdownClose()
  }

  return (
    <Fragment>
      <Badge
        overlap="circular"
        onClick={handleDropdownOpen}
        sx={{ ml: 2, cursor: 'pointer' }}
        badgeContent={<BadgeContentSpan />}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Avatar
          alt={name}
          src={hasAvatar ? (user?.info?.avatar as string) : undefined}
          onClick={handleDropdownOpen}
          sx={getAvatarSx(38, hasAvatar, 14)}
        >
          {name.slice(0, 1).toUpperCase()}
        </Avatar>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleDropdownClose()}
        sx={{ '& .MuiMenu-paper': { width: 230, mt: 4.75 } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: direction === 'ltr' ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: direction === 'ltr' ? 'right' : 'left',
        }}
      >
        <Box sx={{ py: 1.75, px: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Badge
              overlap="circular"
              badgeContent={<BadgeContentSpan />}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
            >
              <Avatar
                alt={name}
                src={hasAvatar ? (user?.info?.avatar as string) : undefined}
                sx={getAvatarSx('2.5rem', hasAvatar, 14)}
              >
                {name.slice(0, 1).toUpperCase()}
              </Avatar>
            </Badge>
            <Box
              sx={{
                display: 'flex',
                ml: 2.5,
                alignItems: 'flex-start',
                flexDirection: 'column',
              }}
            >
              <Typography sx={{ fontWeight: 500 }}>{name}</Typography>
              <Typography variant="body2">{user?.info?.jobTitle}</Typography>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: (theme) => `${theme.spacing(2)} !important` }} />
        <MenuItemStyled
          sx={{ p: 0 }}
          onClick={() => handleNavigate('/settings/personal/profile')}
        >
          <Box sx={styles}>
            <Icon icon="tabler:user-check" />
            My Profile
          </Box>
        </MenuItemStyled>
        <MenuItemStyled
          sx={{ p: 0 }}
          onClick={() => handleNavigate('/settings/project/general')}
        >
          <Box sx={styles}>
            <Icon icon="tabler:settings" />
            Settings
          </Box>
        </MenuItemStyled>
        <MenuItemStyled
          sx={{ p: 0 }}
          onClick={() => handleNavigate('/settings/project/billing')}
        >
          <Box sx={styles}>
            <Icon icon="tabler:credit-card" />
            Billing
          </Box>
        </MenuItemStyled>
        <Divider sx={{ my: (theme) => `${theme.spacing(2)} !important` }} />
        <MenuItemStyled sx={{ p: 0 }} onClick={() => handleDropdownClose()}>
          <Box sx={styles}>
            <Icon icon="tabler:lifebuoy" />
            Help
          </Box>
        </MenuItemStyled>
        <MenuItemStyled sx={{ p: 0 }} onClick={() => handleDropdownClose()}>
          <Box sx={styles}>
            <Icon icon="tabler:info-circle" />
            FAQ
          </Box>
        </MenuItemStyled>
        <MenuItemStyled sx={{ p: 0 }} onClick={() => handleDropdownClose()}>
          <Box sx={styles}>
            <Icon icon="tabler:currency-dollar" />
            Pricing
          </Box>
        </MenuItemStyled>
        <Divider sx={{ my: (theme) => `${theme.spacing(2)} !important` }} />
        <MenuItemStyled sx={{ p: 0 }} onClick={handleLogout}>
          <Box sx={styles}>
            <Icon icon="tabler:logout" />
            Sign Out
          </Box>
        </MenuItemStyled>
      </Menu>
    </Fragment>
  )
}

export default UserDropdown
